import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SaveApiResMessage } from '@/actions';
import filterStyles from '@/ctComponents/Filter/styles.module.css';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import appointmentStyles from '@/ctComponents/AppointmentTable/AppointmentTable.module.css';
import radioStyles from './AutobookRuleCustomerRadio.module.css';
import optionLabelStyles from './AutobookRuleOptionLabel.module.css';
import { AutobookRuleWarehouseGroupsSection } from './AutobookRuleWarehouseGroupsSection';

const deliveryMaxDaysInputStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  fontSize: 14,
  fontFamily: "'Averta', sans-serif",
};

const deliveryMaxDaysLabelClass =
  "font-['Averta',sans-serif] font-bold leading-tight text-[#131517]";

const deliveryMaxDaysLabelStyle = { fontSize: 14 };

const CUSTOMERS_LIST_HEIGHT = 104;
const WAREHOUSES_LIST_HEIGHT_EMPTY = 76;
const WAREHOUSES_LIST_HEIGHT_FILLED = 76;
function fixedListScrollBoxStyle(px) {
  return {
    height: px,
    minHeight: px,
    maxHeight: px,
    boxSizing: 'border-box',
    flex: `0 0 ${px}px`,
  };
}

const customerListScrollBoxStyle = fixedListScrollBoxStyle(CUSTOMERS_LIST_HEIGHT);

const WAREHOUSE_GROUPS_INTRO =
  'A rule can apply to specific warehouses and also one or more warehouse groups. This helps define broader routing logic and shared behavior across related locations.';

function toastScopeSelectionLocked() {
  SaveApiResMessage({
    title: 'Default rule',
    message:
      "You can't uncheck this selection, the default rule includes all customers and warehouses.",
    mode: 'warning',
  });
}

function possessiveName(name) {
  const t = (name || '').trim();
  if (!t) return '';
  const last = t[t.length - 1];
  if (last === 's' || last === 'S') return `${t}'`;
  return `${t}'s`;
}

function warehouseSectionTitle(focusedCustomerId, customersById) {
  if (!focusedCustomerId) return 'Warehouses';
  const label = customersById[focusedCustomerId]?.label ?? 'Customer';
  return `${possessiveName(label)} Warehouses`;
}

function warehouseIdsForCustomer(catalogWarehouses, customerId) {
  return catalogWarehouses
    .filter((w) => w.customerId === customerId)
    .map((w) => w.id);
}

const CustomerMasterCheckboxRow = ({
  customerId,
  label,
  catalogWarehouses,
  selectedWarehouseIds,
  onToggleAllWarehouses,
  onLabelClick,
  isWarehouseListFocused,
}) => {
  const inputRef = useRef(null);
  const warehouseIds = useMemo(
    () => warehouseIdsForCustomer(catalogWarehouses, customerId),
    [catalogWarehouses, customerId]
  );
  const n = warehouseIds.length;
  const selectedCount = useMemo(
    () => warehouseIds.filter((id) => selectedWarehouseIds.has(id)).length,
    [warehouseIds, selectedWarehouseIds]
  );
  const allSelected = n > 0 && selectedCount === n;
  const someSelected = selectedCount > 0 && selectedCount < n;

  useEffect(() => {
    const el = inputRef.current;
    if (el) el.indeterminate = someSelected;
  }, [someSelected, allSelected, n]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${filterStyles.option} ${optionLabelStyles.optionRow} ${radioStyles.customerRow}${
        isWarehouseListFocused ? ` ${radioStyles.customerRowFocused}` : ''
      }`}
      onClick={() => onLabelClick(customerId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLabelClick(customerId);
        }
      }}>
      <input
        ref={inputRef}
        type="checkbox"
        className={`${filterStyles.checkBox} ${radioStyles.checkBoxMaster} shrink-0`}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggleAllWarehouses(customerId)}
      />
      <span
        className={`${optionLabelStyles.labelText} flex-1 min-w-0 max-w-none select-none text-xs`}
        title={label}>
        {label}
      </span>
    </div>
  );
};

const BorderedCustomerCheckboxSection = ({
  title,
  items,
  catalogWarehouses,
  selectedWarehouseIds,
  onToggleAllWarehouses,
  onFocusCustomerByLabel,
  focusedCustomerId,
}) => {
  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col self-start rounded-lg border border-solid border-[#e8e8e8] bg-white p-3">
      <h3 className="m-0 mb-3 shrink-0 truncate text-left text-sm font-bold leading-tight text-[#131517]">
        {title}
      </h3>
      <div
        className="w-full min-w-0 overflow-x-hidden overflow-y-auto pr-1"
        style={customerListScrollBoxStyle}>
        <div
          className="grid min-w-0 gap-2"
          style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          {items.map((item) => (
            <CustomerMasterCheckboxRow
              key={item.id}
              customerId={item.id}
              label={item.label}
              catalogWarehouses={catalogWarehouses}
              selectedWarehouseIds={selectedWarehouseIds}
              onToggleAllWarehouses={onToggleAllWarehouses}
              onLabelClick={onFocusCustomerByLabel}
              isWarehouseListFocused={focusedCustomerId === item.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const BorderedWarehouseChecklistSection = ({
  title,
  items,
  selected,
  onToggle,
  emptyContent,
  showListFooter = true,
}) => {
  const showGrid = items.length > 0;

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col self-start rounded-lg border border-solid border-[#e8e8e8] bg-white p-3">
      <h3 className="m-0 mb-3 shrink-0 truncate text-left text-sm font-bold leading-tight text-[#131517]">
        {title}
      </h3>
      <div
        className="w-full min-w-0 overflow-x-hidden overflow-y-auto pr-1"
        style={fixedListScrollBoxStyle(
          showGrid ? WAREHOUSES_LIST_HEIGHT_FILLED : WAREHOUSES_LIST_HEIGHT_EMPTY
        )}>
        {showGrid ? (
          <div
            className="grid min-w-0"
            style={{
              gap: '0.03rem',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            }}>
            {items.map((item) => (
              <label
                key={item.id}
                className={`${filterStyles.option} ${optionLabelStyles.optionRow} ${radioStyles.warehouseRow}`}>
                <input
                  type="checkbox"
                  className={`${filterStyles.checkBox} shrink-0`}
                  checked={selected.has(item.id)}
                  onChange={() => onToggle(item.id)}
                />
                <span
                  className={`${optionLabelStyles.labelText} text-xs`}
                  title={item.label}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        ) : (
          emptyContent
        )}
      </div>
      {showListFooter && (
        <p className="mb-0 mt-3 shrink-0 text-left text-xs leading-snug text-inactive-text">
          {selected.size} selected
        </p>
      )}
    </section>
  );
};

export const AutobookRuleStepWarehouseCustomer = ({
  deliveryTypeOption,
  setDeliveryTypeOption,
  maximumDaysAfterWindow,
  setMaximumDaysAfterWindow,
  deliveryTypeOptions,
  catalogCustomers = [],
  catalogWarehouses = [],
  catalogWarehouseGroups = [],
  selectedWarehouseCatalogIds = [],
  setSelectedWarehouseCatalogIds,
  selectedGroupCatalogIds = [],
  setSelectedGroupCatalogIds,
  draftRuleUuid,
  scopeSelectionLocked = false,
}) => {
  const customersById = useMemo(() => {
    const m = {};
    catalogCustomers.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [catalogCustomers]);

  const [focusedCustomerId, setFocusedCustomerId] = useState(null);

  const selectedWarehouseIds = useMemo(
    () => new Set(selectedWarehouseCatalogIds.map(String)),
    [selectedWarehouseCatalogIds]
  );
  useEffect(() => {
    setFocusedCustomerId(null);
  }, [draftRuleUuid, catalogWarehouseGroups]);

  const focusCustomerByLabel = useCallback((customerId) => {
    setFocusedCustomerId(customerId);
  }, []);

  const toggleAllWarehousesForCustomer = useCallback(
    (customerId) => {
      if (typeof setSelectedWarehouseCatalogIds !== 'function') return;
      const ids = warehouseIdsForCustomer(catalogWarehouses, customerId);
      if (ids.length === 0) return;
      const allOn = ids.every((wid) =>
        selectedWarehouseIds.has(String(wid))
      );
      if (scopeSelectionLocked) {
        if (allOn) toastScopeSelectionLocked();
        return;
      }
      setSelectedWarehouseCatalogIds((prev) => {
        const next = new Set((prev ?? []).map(String));
        const everyOn = ids.every((wid) => next.has(String(wid)));
        if (everyOn) {
          ids.forEach((wid) => next.delete(String(wid)));
        } else {
          ids.forEach((wid) => next.add(String(wid)));
        }
        return [...next];
      });
    },
    [
      catalogWarehouses,
      setSelectedWarehouseCatalogIds,
      scopeSelectionLocked,
      selectedWarehouseIds,
    ]
  );

  const toggleWarehouse = useCallback(
    (id) => {
      if (typeof setSelectedWarehouseCatalogIds !== 'function') return;
      const sid = String(id);
      if (scopeSelectionLocked) {
        if (selectedWarehouseIds.has(sid)) toastScopeSelectionLocked();
        return;
      }
      setSelectedWarehouseCatalogIds((prev) => {
        const next = new Set((prev ?? []).map(String));
        if (next.has(sid)) next.delete(sid);
        else next.add(sid);
        return [...next];
      });
    },
    [setSelectedWarehouseCatalogIds, scopeSelectionLocked, selectedWarehouseIds]
  );

  const visibleWarehouses = useMemo(() => {
    if (!focusedCustomerId) return [];
    return catalogWarehouses.filter(
      (w) => w.customerId === focusedCustomerId
    );
  }, [focusedCustomerId, catalogWarehouses]);

  const warehouseTitle = warehouseSectionTitle(
    focusedCustomerId,
    customersById
  );

  const warehouseEmpty = (
    <div className="flex h-full min-h-0 items-center justify-center px-2 text-center">
      <p className="m-0 max-w-[220px] text-xs font-bold leading-snug text-[#99a0ae]">
        Click a customer name to view their warehouses
      </p>
    </div>
  );

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-5">
      <div className="flex w-full min-w-0 max-w-full flex-row items-start gap-5">
        <BorderedCustomerCheckboxSection
          title="Customers"
          items={catalogCustomers}
          catalogWarehouses={catalogWarehouses}
          selectedWarehouseIds={selectedWarehouseIds}
          onToggleAllWarehouses={toggleAllWarehousesForCustomer}
          onFocusCustomerByLabel={focusCustomerByLabel}
          focusedCustomerId={focusedCustomerId}
        />
        <BorderedWarehouseChecklistSection
          title={warehouseTitle}
          items={visibleWarehouses}
          selected={selectedWarehouseIds}
          onToggle={toggleWarehouse}
          emptyContent={warehouseEmpty}
          showListFooter
        />
      </div>

      <AutobookRuleWarehouseGroupsSection
        instanceId="rule-scope"
        sectionTitle="Warehouse Groups"
        intro={WAREHOUSE_GROUPS_INTRO}
        subsectionTitle="Apply Rule to Warehouse Groups"
        catalogWarehouseGroups={catalogWarehouseGroups}
        selectedGroupCatalogIds={selectedGroupCatalogIds}
        setSelectedGroupCatalogIds={setSelectedGroupCatalogIds}
        catalogCustomers={catalogCustomers}
        catalogWarehouses={catalogWarehouses}
        draftRuleUuid={draftRuleUuid}
      />

      <section className="box-border flex w-full min-w-0 max-w-full flex-col rounded-lg border border-solid border-[#e8e8e8] bg-white p-4">
        <div className="flex min-w-0 max-w-full flex-row flex-wrap items-start gap-2">
          <div className={`w-96 min-w-0 shrink-0`}>
            <CustomSelectEx
              height={48}
              maxHeight={48}
              title="Delivery Type"
              placeholder="Delivery Type"
              value={deliveryTypeOption}
              onChange={(item) =>
                setDeliveryTypeOption(item ?? deliveryTypeOptions[0])
              }
              options={deliveryTypeOptions}
              dropdown
              width="100%"
            />
          </div>
          <div className="flex w-96 min-w-0 shrink-0 flex-col gap-2">
            <span
              className={deliveryMaxDaysLabelClass}
              style={deliveryMaxDaysLabelStyle}>
              Maximum X Days After the Window
            </span>
            <input
              type="number"
              className={appointmentStyles.LicenseInput}
              value={maximumDaysAfterWindow}
              onChange={(e) => setMaximumDaysAfterWindow(e.target.value)}
              placeholder="Days"
              min={0}
              style={deliveryMaxDaysInputStyle}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
